(() => {
  const cleanups = new WeakMap();

  const setupHeader = (root) => {
    if (!root || root.dataset.panchaReady === 'true') return;

    const section = root.closest('.shopify-section') || root;
    const inner = root.querySelector('.header-bottom--wrapper');
    const navigation = root.querySelector('[data-pancha-nav]');
    const actions = root.querySelector('.pancha-header__actions');
    const menuButton = root.querySelector('[data-mobile-menu]');
    const searchButtons = document.querySelectorAll('[data-search-mobile]');
    const cartDrawer = document.getElementById('halo-cart-sidebar');
    const cartTriggers = document.querySelectorAll('[data-cart-sidebar]');
    const categoryMenus = [...root.querySelectorAll('[data-pancha-categories]')];
    const mobileMenu = document.getElementById('navigation-mobile');
    let menuWasOpen = false;
    let cartWasOpen = false;
    let cartReturnFocus = null;
    let lastScrollY = window.scrollY;
    let ticking = false;
    let lastTrigger = null;

    const restoreNavigation = () => {
      if (!navigation || !inner || root.contains(navigation)) return;

      if (actions) {
        inner.insertBefore(navigation, actions);
      } else {
        inner.appendChild(navigation);
      }
    };

    const updateOverlayState = () => {
      const menuIsOpen = document.body.classList.contains('menu_open');
      if (menuIsOpen && navigation && !mobileMenu?.contains(navigation)) {
        mobileMenu?.querySelector('[data-navigation-mobile]')?.appendChild(navigation);
      }
      menuButton?.setAttribute(
        'aria-expanded',
        String(menuIsOpen)
      );
      if (menuIsOpen && !menuWasOpen) {
        section.classList.remove('pancha-header-is-hidden');
        window.requestAnimationFrame(() => mobileMenu?.querySelector('[data-menu-close-sidebar]')?.focus({ preventScroll: true }));
      } else if (!menuIsOpen && menuWasOpen) {
        categoryMenus.forEach((menu) => { menu.open = false; });
        if (mobileMenu?.contains(document.activeElement)) menuButton?.focus({ preventScroll: true });
      }
      menuWasOpen = menuIsOpen;
      searchButtons.forEach((button) => button.setAttribute(
        'aria-expanded',
        document.body.classList.contains('open_search_mobile') ? 'true' : 'false'
      ));

      if (!cartDrawer) return;
      const cartIsOpen = document.body.classList.contains('cart-sidebar-show');
      cartDrawer.setAttribute('aria-hidden', String(!cartIsOpen));
      cartTriggers.forEach((trigger) => {
        trigger.setAttribute('aria-controls', cartDrawer.id);
        trigger.setAttribute('aria-expanded', String(cartIsOpen));
      });
      if (cartIsOpen && !cartWasOpen) {
        cartReturnFocus = document.activeElement;
        section.classList.remove('pancha-header-is-hidden');
        window.requestAnimationFrame(() => {
          if (document.body.classList.contains('cart-sidebar-show')) {
            cartDrawer.querySelector('[data-close-cart-sidebar]')?.focus({ preventScroll: true });
          }
        });
      } else if (!cartIsOpen && cartWasOpen && cartReturnFocus?.isConnected) {
        cartReturnFocus.focus({ preventScroll: true });
      }
      cartWasOpen = cartIsOpen;
    };

    const updateCartCount = (event) => {
      root.querySelectorAll('.pancha-header__cart-count').forEach((badge) => {
        const count = event?.detail?.item_count ?? Number(badge.textContent.trim());
        badge.classList.toggle('is-empty', count === 0);
      });
      if (event && cartWasOpen && (document.activeElement === document.body || document.activeElement === cartReturnFocus)) {
        cartDrawer?.querySelector('[data-close-cart-sidebar]')?.focus({ preventScroll: true });
      }
    };

    const handleTriggerClick = (event) => {
      if (event.target.closest('[data-search-close-sidebar]')) {
        event.preventDefault();
        document.body.classList.remove('open_search_mobile');
        lastTrigger?.focus({ preventScroll: true });
        return;
      }
      const trigger = event.target.closest(
        '[data-mobile-menu], [data-search-mobile], [data-cart-sidebar], [data-open-auth-sidebar]'
      );

      if (!trigger) return;

      lastTrigger = trigger;

      if (trigger.matches('[data-search-mobile]')) {
        event.preventDefault();
        document.body.classList.remove('menu_open', 'cart-sidebar-show', 'auth-sidebar-show');
        document.body.classList.add('open_search_mobile');
        const searchDetails = document.querySelector('#search-form-mobile .search_details');
        if (searchDetails) searchDetails.open = true;

        window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
          if (document.body.classList.contains('open_search_mobile')) {
            document.querySelector('#search-form-mobile input[type="search"]')?.focus({ preventScroll: true });
          }
        }));
      }
    };

    const handleScroll = () => {
      if (ticking || (window.Shopify && window.Shopify.designMode)) return;

      ticking = true;
      window.requestAnimationFrame(() => {
        const currentScrollY = Math.max(window.scrollY, 0);
        const overlayOpen = [
          'menu_open',
          'open_search_mobile',
          'cart-sidebar-show',
          'auth-sidebar-show'
        ].some((className) => document.body.classList.contains(className));

        if (overlayOpen || currentScrollY < 120 || currentScrollY < lastScrollY) {
          section.classList.remove('pancha-header-is-hidden');
        } else if (currentScrollY > lastScrollY + 4) {
          section.classList.add('pancha-header-is-hidden');
        }

        lastScrollY = currentScrollY;
        ticking = false;
      });
    };

    const handleResize = () => {
      if (window.innerWidth <= 1024) return;

      document.body.classList.remove('menu_open');
      restoreNavigation();
      section.classList.remove('pancha-header-is-hidden');
    };

    const handleKeydown = (event) => {
      if (event.key === 'Tab' && cartWasOpen && (cartDrawer?.contains(document.activeElement) || document.activeElement === document.body)) {
        const focusable = [...cartDrawer.querySelectorAll('a[href], button, input, select, textarea, iframe, [tabindex="0"]')]
          .filter((element) => !element.disabled && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (first && document.activeElement === document.body) {
          event.preventDefault();
          (event.shiftKey ? last : first).focus();
        } else if (first && event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (last && !event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
      if (event.key !== 'Escape') return;

      const openCategory = categoryMenus.find((menu) => menu.open);
      if (openCategory) {
        openCategory.open = false;
        openCategory.querySelector('summary')?.focus({ preventScroll: true });
        event.preventDefault();
        return;
      }

      const openClasses = [
        'menu_open',
        'open_search_mobile',
        'open_search_menu',
        'cart-sidebar-show',
        'auth-sidebar-show'
      ];
      const hadOpenOverlay = openClasses.some((className) =>
        document.body.classList.contains(className)
      );

      openClasses.forEach((className) => document.body.classList.remove(className));
      restoreNavigation();

      if (hadOpenOverlay && !cartWasOpen && lastTrigger) {
        lastTrigger.focus();
      }
    };

    const handleNavigationClick = (event) => {
      if (!navigation?.contains(event.target)) {
        categoryMenus.forEach((menu) => { menu.open = false; });
      } else if (event.target.closest('a[href]')) {
        categoryMenus.forEach((menu) => { menu.open = false; });
        document.body.classList.remove('menu_open');
        restoreNavigation();
      }
    };

    const handleNavigationFocus = (event) => {
      if (window.innerWidth > 1024 && !navigation?.contains(event.target)) {
        categoryMenus.forEach((menu) => { menu.open = false; });
      }
    };

    document.addEventListener('click', handleTriggerClick);
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('cart-update', updateCartCount);
    document.addEventListener('click', handleNavigationClick);
    document.addEventListener('focusin', handleNavigationFocus);

    const bodyObserver = new MutationObserver(updateOverlayState);
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    updateOverlayState();
    updateCartCount();

    root.dataset.panchaReady = 'true';
    cleanups.set(root, () => {
      document.removeEventListener('click', handleTriggerClick);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('cart-update', updateCartCount);
      document.removeEventListener('click', handleNavigationClick);
      document.removeEventListener('focusin', handleNavigationFocus);
      bodyObserver.disconnect();
      root.removeAttribute('data-pancha-ready');
    });
  };

  const initializeHeaders = (scope = document) => {
    if (scope.matches?.('[data-pancha-sticky-header]')) {
      setupHeader(scope);
    }

    scope.querySelectorAll?.('[data-pancha-sticky-header]').forEach(setupHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeHeaders(), { once: true });
  } else {
    initializeHeaders();
  }

  document.addEventListener('shopify:section:load', (event) => initializeHeaders(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-pancha-sticky-header]').forEach((root) => {
      cleanups.get(root)?.();
      cleanups.delete(root);
    });
  });
})();
